import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Select } from '../../components/ui/Select'
import { Card } from '../../components/ui/Card'
import { ProductsService } from '../../services/api/productsService'
import { BrandsService } from '../../services/api/brandsService'
import { useToast } from '../../hooks/useToast'
import { useDebounce } from '../../hooks/useDebounce'
import { useCategories } from '../../hooks/useCategories'
import { useCertifications } from '../../hooks/useCertifications'
import { useContinents } from '../../hooks/useContinents'
import { useProductDetails } from '../../hooks/useProductDetails'
import { IngredientsService } from '../../services/api/ingredientsService'
import type { Product, ProductImage, ProductImageType } from '../../types/product'
import type { Brand } from '../../types/brand'
import type { ProductCategory, SubCategory, GalenicForm } from '../../types/category'
import type { Certification } from '../../types/certification'
import type { Continent, Country } from '../../types/continent'
import type { Ingredient } from '../../types/ingredient'
import { TrashIcon, SparklesIcon, DocumentCheckIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import { ImageMagnifier } from '../../components/products/ImageMagnifier'
import { ImagePreviewModal } from '../../components/products/ImagePreviewModal'
import { ToastContainer } from '../../components/ui/Toast'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'

const IMAGE_TYPES = {
  FRONT: 'front' as ProductImageType,
  BACK: 'back' as ProductImageType,
  DETAILED: 'detailed' as ProductImageType,
}

const IMAGE_LABELS = {
  FRONT: 'Face avant',
  BACK: 'Ingrédients (1)',
  DETAILED: 'Ingrédients (2)',
}

interface ProductFormData {
  brandUUID?: string
  categoryId?: number
  subCategoryId?: number
  galenicFormId?: number
  manufacturingContinentId?: number
  images: ProductImage[]
  ingredients: Ingredient[]
  certifications: Certification[]
  toValidate: boolean
}

export const ProductDetailsPage: React.FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { toasts, showToast, removeToast } = useToast()

  // Use cached hooks for static data
  const { categories } = useCategories()
  const { continents } = useContinents()
  const { certifications } = useCertifications()
  const { product, isLoading, refetch: refetchProduct } = useProductDetails(id)

  const [isSaving, setIsSaving] = useState(false)
  const [isExtracting, setIsExtracting] = useState(false)
  const [, setIsLoadingProduct] = useState(false)
  const [previewImage, setPreviewImage] = useState<ProductImage | null>(null)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  // Form data
  const [formData, setFormData] = useState<ProductFormData>({
    images: [],
    ingredients: [],
    certifications: [],
    toValidate: true
  })

  // Options data - brands loaded dynamically via search
  // const [brands] = useState<Brand[]>([]) // Removed unused variable

  // Selected options
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<ProductCategory | null>(null)
  const [selectedSubCategory, setSelectedSubCategory] = useState<SubCategory | null>(null)
  const [selectedGalenicForm, setSelectedGalenicForm] = useState<GalenicForm | null>(null)
  const [selectedContinent, setSelectedContinent] = useState<Continent | null>(null)
  const [, setSelectedCountry] = useState<Country | null>(null)
  const [countrySearchQuery, setCountrySearchQuery] = useState('')
  const [showCountryDropdown, setShowCountryDropdown] = useState(false)

  // Search states
  const [brandSearchQuery, setBrandSearchQuery] = useState('')
  const [brandSearchResults, setBrandSearchResults] = useState<Brand[]>([])
  const [showBrandResults, setShowBrandResults] = useState(false)
  const [ingredientSearchQuery, setIngredientSearchQuery] = useState('')
  const [ingredientSearchResults, setIngredientSearchResults] = useState<Ingredient[]>([])
  const [showIngredientResults, setShowIngredientResults] = useState(false)

  // Flag to prevent search on initial load
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [hasUserInteractedWithBrandField, setHasUserInteractedWithBrandField] = useState(false)

  // Debounced search queries
  const debouncedBrandSearch = useDebounce(brandSearchQuery, 500)
  const debouncedIngredientSearch = useDebounce(ingredientSearchQuery, 500)
  const debouncedCountrySearch = useDebounce(countrySearchQuery, 300)

  const brandSearchRef = useRef<HTMLDivElement>(null)
  const ingredientSearchRef = useRef<HTMLDivElement>(null)
  const countrySearchRef = useRef<HTMLDivElement>(null)

  const isNewProduct = !id
  const isProductValidated = product ? !product.toValidate : true

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Populate form when product data is available
  useEffect(() => {
    if (product && categories.length > 0 && continents.length > 0) {
      populateFormFromProduct(product, categories, continents)
      // Reset the initial load flag after a delay to allow form population
      setTimeout(() => {
        setIsInitialLoad(false)
      }, 1000)
    }
  }, [product, categories, continents])

  useEffect(() => {
    // Close search dropdowns when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      if (brandSearchRef.current && !brandSearchRef.current.contains(event.target as Node)) {
        setShowBrandResults(false)
      }
      if (ingredientSearchRef.current && !ingredientSearchRef.current.contains(event.target as Node)) {
        setShowIngredientResults(false)
      }
      if (countrySearchRef.current && !countrySearchRef.current.contains(event.target as Node)) {
        setShowCountryDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Listen for ingredient updates (when coming back from validation)
  useEffect(() => {
    const handleIngredientUpdated = async () => {
      if (id) {
        // Reload the product to get the updated ingredients list
        await loadProduct()
      }
    }

    window.addEventListener('ingredientUpdated', handleIngredientUpdated)
    return () => {
      window.removeEventListener('ingredientUpdated', handleIngredientUpdated)
    }
  }, [id])


  const loadProduct = async () => {
    if (!id) return

    try {
      setIsLoadingProduct(true)
      const productData = await ProductsService.getProduct(id)
      populateFormFromProduct(productData)
      return productData
    } catch (error) {
      console.error('Failed to load product:', error)
      throw error
    } finally {
      setIsLoadingProduct(false)
    }
  }

  const populateFormFromProduct = (productData: Product, categoriesData?: ProductCategory[], continentsData?: Continent[]) => {
    const categoriesToUse = categoriesData || categories
    const continentsToUse = continentsData || continents

    setFormData({
      brandUUID: productData.brandUUID,
      categoryId: productData.categoryId,
      subCategoryId: productData.subCategoryId,
      galenicFormId: productData.galenicFormId,
      manufacturingContinentId: productData.manufacturingContinentId,
      images: productData.images || [],
      ingredients: productData.ingredients || [],
      certifications: productData.certifications || [],
      toValidate: productData.toValidate || false
    })

    // Set selected brand
    if (productData.brandUUID && productData.brandName) {
      setSelectedBrand({
        uuid: productData.brandUUID,
        name: productData.brandName,
        toValidate: false
      })
      setBrandSearchQuery(productData.brandName) // This won't trigger search because hasUserInteractedWithBrandField is false
    }

    // Set selected category and subcategory
    if (productData.categoryId) {
      const category = categoriesToUse.find(c => c.id === productData.categoryId)
      if (category) {
        setSelectedCategory(category)

        if (productData.subCategoryId) {
          const subCategory = category.subCategories.find(s => s.id === productData.subCategoryId)
          if (subCategory) {
            setSelectedSubCategory(subCategory)

            if (productData.galenicFormId) {
              const galenicForm = subCategory.galenicForms.find(g => g.id === productData.galenicFormId)
              if (galenicForm) {
                setSelectedGalenicForm(galenicForm)
              }
            }
          }
        }
      }
    }

    // Set selected continent only (country is not stored)
    if (productData.manufacturingContinentId) {
      const continent = continentsToUse.find(c => c.id === productData.manufacturingContinentId)
      if (continent) {
        setSelectedContinent(continent)
      }
    }
  }

  // Effect for brand search with debounce
  useEffect(() => {
    // Only search if user has actually interacted with the brand field
    if (!hasUserInteractedWithBrandField) {
      return
    }

    if (debouncedBrandSearch.length < 3) {
      setBrandSearchResults([])
      setShowBrandResults(false)
      return
    }

    const searchBrands = async () => {
      try {
        const results = await BrandsService.searchBrands(debouncedBrandSearch)
        setBrandSearchResults(results)
        setShowBrandResults(true)
      } catch (error) {
        console.error('Failed to search brands:', error)
      }
    }

    searchBrands()
  }, [debouncedBrandSearch, hasUserInteractedWithBrandField])

  const handleBrandSearch = (query: string) => {
    setBrandSearchQuery(query)
    // Mark that user has interacted with the brand field
    if (!hasUserInteractedWithBrandField) {
      setHasUserInteractedWithBrandField(true)
    }
    // Mark that initial load is complete when user starts typing
    if (isInitialLoad) {
      setIsInitialLoad(false)
    }
  }

  const handleBrandSelect = (brand: Brand) => {
    setSelectedBrand(brand)
    setBrandSearchQuery(brand.name)
    setShowBrandResults(false)
    setFormData(prev => ({ ...prev, brandUUID: brand.uuid }))
  }

  // Effect for ingredient search with debounce
  useEffect(() => {
    // Skip search on initial load
    if (isInitialLoad) {
      return
    }

    if (debouncedIngredientSearch.length < 2) {
      setIngredientSearchResults([])
      setShowIngredientResults(false)
      return
    }

    const searchIngredients = async () => {
      try {
        const results = await IngredientsService.searchIngredients(debouncedIngredientSearch, {
          limit: 200,
          toValidate: false
        })
        setIngredientSearchResults(results)
        setShowIngredientResults(true)
      } catch (error) {
        console.error('Failed to search ingredients:', error)
      }
    }

    searchIngredients()
  }, [debouncedIngredientSearch, isInitialLoad])

  const handleIngredientSearch = (query: string) => {
    setIngredientSearchQuery(query)
    // Mark that initial load is complete when user starts typing
    if (isInitialLoad) {
      setIsInitialLoad(false)
    }
  }

  const handleIngredientSelect = (ingredient: Ingredient) => {
    const isAlreadySelected = formData.ingredients.some(ing => ing.uuid === ingredient.uuid)

    if (!isAlreadySelected) {
      const newIngredients = [...formData.ingredients, ingredient]
        .sort((a, b) => a.inciName.toLowerCase().localeCompare(b.inciName.toLowerCase()))

      setFormData(prev => ({ ...prev, ingredients: newIngredients }))
    }

    setIngredientSearchQuery('')
    setShowIngredientResults(false)
  }

  const removeIngredient = (ingredientUuid: string) => {
    const newIngredients = formData.ingredients.filter(ing => ing.uuid !== ingredientUuid)
    setFormData(prev => ({ ...prev, ingredients: newIngredients }))
  }

  const handleImageUpload = (type: ProductImageType, file: File) => {
    const reader = new FileReader()
    reader.onload = () => {
      const base64 = reader.result as string
      // Remove the data URL prefix to get only the base64 string
      const base64Data = base64.split(',')[1]
      const newImage: ProductImage = {
        imageType: type,
        image: base64Data,
      }

      const updatedImages = formData.images.filter(img => img.imageType !== type)
      updatedImages.push(newImage)

      setFormData(prev => ({ ...prev, images: updatedImages }))
    }
    reader.readAsDataURL(file)
  }

  const removeImage = (type: ProductImageType) => {
    const updatedImages = formData.images.filter(img => img.imageType !== type)
    setFormData(prev => ({ ...prev, images: updatedImages }))
  }

  const handleCertificationToggle = (certification: Certification) => {
    const isSelected = formData.certifications.some(c => c.id === certification.id)

    let newCertifications: Certification[]
    if (isSelected) {
      newCertifications = formData.certifications.filter(c => c.id !== certification.id)
    } else {
      newCertifications = [...formData.certifications, certification]
    }

    setFormData(prev => ({ ...prev, certifications: newCertifications }))
  }

  const handleCountrySelect = (country: Country) => {
    setSelectedCountry(country)
    setCountrySearchQuery(country.name)
    setShowCountryDropdown(false)

    // Find and set the corresponding continent
    const continent = continents.find(c => c.countries.some(co => co.id === country.id))
    if (continent) {
      setSelectedContinent(continent)
      setFormData(prev => ({ ...prev, manufacturingContinentId: continent.id }))
    }
  }

  const getFilteredCountries = (): Country[] => {
    // Get all countries from all continents
    const allCountries = continents.flatMap(c => c.countries)

    if (debouncedCountrySearch.trim() === '') {
      // If continent is selected, show only countries from that continent
      if (selectedContinent) {
        return selectedContinent.countries
      }
      return allCountries
    }

    // Filter by search query using debounced value
    const query = debouncedCountrySearch.toLowerCase()
    const filtered = allCountries.filter(country =>
      country.name.toLowerCase().includes(query)
    )

    // If continent is selected, further filter by continent
    if (selectedContinent) {
      return filtered.filter(country =>
        selectedContinent.countries.some(c => c.id === country.id)
      )
    }

    return filtered
  }

  const handleExtractIngredients = async () => {
    if (!id || isNewProduct) return

    try {
      setIsExtracting(true)
      await ProductsService.extractIngredients(id)

      // Reload product to get updated ingredients
      await loadProduct()

      showToast('Extraction d\'ingrédients réalisée avec succès', 'success')
    } catch (error) {
      console.error('Failed to extract ingredients:', error)
      showToast('Erreur lors du lancement de l\'extraction d\'ingrédients', 'error')
    } finally {
      setIsExtracting(false)
    }
  }

  const handleSubmit = async (isDraft: boolean = false) => {
    if (!validateForm()) return

    try {
      setIsSaving(true)

      let productData: Partial<Product>

      if (isNewProduct) {
        // For new products, send all data
        productData = {
          ...formData,
          // Send certifications as complete objects like Flutter
          certifications: formData.certifications || [],
          toValidate: isDraft,
        }
      } else {
        // For updates, only send changed fields
        productData = {
          uuid: id,
          brandUUID: formData.brandUUID,
          categoryId: formData.categoryId,
          subCategoryId: formData.subCategoryId,
          galenicFormId: formData.galenicFormId,
          manufacturingContinentId: formData.manufacturingContinentId,
          ingredients: formData.ingredients,
          // Send certifications as complete objects like Flutter
          certifications: formData.certifications || [],
          toValidate: isDraft,
        }

        // Only include images if they have changed
        // Compare the current images with the original product images
        const imagesChanged = product && (
          formData.images.length !== product.images?.length ||
          formData.images.some((img, idx) => {
            const origImg = product.images?.[idx]
            return !origImg ||
              img.imageType !== origImg.imageType ||
              img.image !== origImg.image ||
              img.imageUrl !== origImg.imageUrl
          })
        )

        if (imagesChanged || !product) {
          productData.images = formData.images
        }
      }

      let success = false
      if (isNewProduct) {
        await ProductsService.createProduct(productData)
        success = true
      } else {
        await ProductsService.updateProduct(id!, productData)
        success = true
      }

      if (success) {
        const message = isNewProduct
          ? (isDraft ? 'Brouillon enregistré avec succès' : 'Produit créé avec succès')
          : (isDraft ? 'Brouillon enregistré avec succès' :
            (product?.toValidate ? 'Produit validé avec succès' : 'Produit mis à jour avec succès'))

        // Always notify the products list to refresh, especially after validation
        window.dispatchEvent(new CustomEvent('productUpdated', {
          detail: {
            action: isNewProduct ? 'created' : (isDraft ? 'draft' : (product?.toValidate && !isDraft) ? 'validated' : 'updated'),
            productId: id,
            toValidate: isDraft
          }
        }))

        if (isNewProduct || (product?.toValidate && !isDraft)) {
          // Navigate to products list after creation or validation
          navigate('/products', {
            state: {
              showToast: true,
              toastMessage: message,
              toastType: 'success',
              triggerRefresh: true
            }
          })
        } else if (isDraft) {
          // Stay on page for drafts
          showToast(message, 'success')
          // Refresh the product data
          await refetchProduct()
        } else {
          // Navigate to list after regular update
          navigate('/products', {
            state: {
              showToast: true,
              toastMessage: message,
              toastType: 'success',
              triggerRefresh: true
            }
          })
        }
      }
    } catch (error) {
      console.error('Failed to save product:', error)
      showToast('Erreur lors de l\'enregistrement du produit', 'error')
    } finally {
      setIsSaving(false)
    }
  }

  const validateForm = (): boolean => {
    if (!selectedBrand) {
      showToast('Veuillez sélectionner une marque', 'warning')
      return false
    }

    if (!formData.images.some(img => img.imageType === IMAGE_TYPES.FRONT)) {
      showToast('Veuillez ajouter une image de face avant', 'warning')
      return false
    }

    if (!selectedCategory) {
      showToast('Veuillez sélectionner une catégorie', 'warning')
      return false
    }

    if (!selectedSubCategory) {
      showToast('Veuillez sélectionner une sous-catégorie', 'warning')
      return false
    }

    if (selectedSubCategory.galenicForms.length > 0 && !selectedGalenicForm) {
      showToast('Veuillez sélectionner une forme galénique', 'warning')
      return false
    }

    return true
  }

  const handleDelete = () => {
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!id || !product) return

    try {
      await ProductsService.deleteProduct(id)

      // Navigate with state to show toast and trigger refresh on the list page
      navigate('/products', {
        state: {
          showToast: true,
          toastMessage: 'Produit supprimé avec succès',
          toastType: 'success',
          triggerRefresh: true
        }
      })
    } catch (error) {
      console.error('Failed to delete product:', error)
      showToast('Erreur lors de la suppression du produit', 'error')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600 dark:border-gray-400"></div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <ToastContainer toasts={toasts} onClose={removeToast} />

      {/* Image Preview Modal */}
      <ImagePreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        images={formData.images.filter(img => img.imageType !== 'front_bg_removed')}
        currentIndex={formData.images.filter(img => img.imageType !== 'front_bg_removed').findIndex(img => img === previewImage)}
        onImageChange={(index) => {
          const filteredImages = formData.images.filter(img => img.imageType !== 'front_bg_removed')
          setPreviewImage(filteredImages[index])
        }}
      />
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate('/products')}
            className="flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Retour
          </Button>
          <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">
            {isNewProduct ? 'Nouveau produit' : 'Modifier le produit'}
          </h1>
        </div>
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={() => handleSubmit(true)}
            disabled={isSaving}
            className="flex items-center gap-2"
          >
            <DocumentCheckIcon className="h-5 w-5" />
            {isSaving ? 'Enregistrement...' : 'Enregistrer le brouillon'}
          </Button>
          <Button
            onClick={() => handleSubmit(false)}
            disabled={isSaving}
            className={`flex items-center gap-2 ${!isProductValidated ? 'bg-green-600 hover:bg-green-700' : ''}`}
          >
            <CheckCircleIcon className="h-5 w-5" />
            {isSaving
              ? 'Enregistrement...'
              : (!isProductValidated
                ? 'Valider le nouveau produit'
                : 'Enregistrer')
            }
          </Button>
          {!isNewProduct && (
            <Button
              variant="outline"
              onClick={handleDelete}
              disabled={isSaving}
              className="border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 p-2"
              title="Supprimer le produit"
            >
              <TrashIcon className="h-5 w-5" />
            </Button>
          )}
        </div>
      </div>

      {!isProductValidated && (
        <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-orange-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <p className="text-orange-800 dark:text-orange-300">
              Ce produit est en attente de validation. Veuillez vérifier et valider les informations avant de continuer.
            </p>
          </div>
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); handleSubmit(false) }} className="space-y-8">
        {/* Images Section */}
        <Card className="p-6">
          <h2 className="text-xl font-medium text-gray-900 dark:text-white mb-4">Images</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Object.entries(IMAGE_TYPES).map(([key, type]) => (
              <ImageUploadSection
                key={type}
                type={type}
                title={IMAGE_LABELS[key as keyof typeof IMAGE_LABELS]}
                image={formData.images.find(img => img.imageType === type)}
                onUpload={(file) => handleImageUpload(type, file)}
                onRemove={() => removeImage(type)}
                onPreview={(image) => {
                  setPreviewImage(image)
                  setIsPreviewOpen(true)
                }}
              />
            ))}
          </div>
        </Card>

        {/* Brand and Category Section */}
        <Card className="p-6">
          <h2 className="text-xl font-medium text-gray-900 dark:text-white mb-4">Marque et catégorie</h2>
          <div className="space-y-4">
            {/* Brand Search */}
            <div className="relative" ref={brandSearchRef}>
              <label htmlFor="brand-search" className="block text-sm font-normal text-gray-700 dark:text-gray-300 mb-2">
                Marque *
              </label>
              <Input
                id="brand-search"
                type="text"
                value={brandSearchQuery}
                onChange={(e) => handleBrandSearch(e.target.value)}
                placeholder="Rechercher une marque..."
                className="w-full"
              />
              {showBrandResults && brandSearchResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
                  {brandSearchResults.map(brand => (
                    <button
                      key={brand.uuid}
                      type="button"
                      onClick={() => handleBrandSelect(brand)}
                      className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 border-b dark:border-gray-700 last:border-b-0"
                    >
                      <div className="font-normal text-gray-900 dark:text-white">{brand.name}</div>
                      {brand.toValidate && (
                        <div className="text-xs text-red-600 dark:text-red-400">À valider</div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Category */}
            <div>
              <label htmlFor="category-select" className="block text-sm font-normal text-gray-700 dark:text-gray-300 mb-2">
                Catégorie *
              </label>
              <Select
                value={selectedCategory?.id?.toString() || ''}
                onChange={(value) => {
                  const category = categories.find(c => c.id === parseInt(value))
                  setSelectedCategory(category || null)
                  setSelectedSubCategory(null)
                  setSelectedGalenicForm(null)
                  setFormData(prev => ({
                    ...prev,
                    categoryId: category?.id,
                    subCategoryId: undefined,
                    galenicFormId: undefined
                  }))
                }}
                options={[
                  { value: '', label: 'Sélectionnez une catégorie' },
                  ...categories.map(cat => ({
                    value: cat.id!.toString(),
                    label: cat.name
                  }))
                ]}
              />
            </div>

            {/* SubCategory */}
            {selectedCategory && selectedCategory.subCategories.length > 0 && (
              <div>
                <label htmlFor="subcategory-select" className="block text-sm font-normal text-gray-700 dark:text-gray-300 mb-2">
                  Sous-catégorie *
                </label>
                <Select
                  value={selectedSubCategory?.id?.toString() || ''}
                  onChange={(value) => {
                    const subCategory = selectedCategory.subCategories.find(s => s.id === parseInt(value))
                    setSelectedSubCategory(subCategory || null)
                    setSelectedGalenicForm(null)
                    setFormData(prev => ({
                      ...prev,
                      subCategoryId: subCategory?.id,
                      galenicFormId: undefined
                    }))
                  }}
                  options={[
                    { value: '', label: 'Sélectionnez une sous-catégorie' },
                    ...selectedCategory.subCategories.map(sub => ({
                      value: sub.id!.toString(),
                      label: sub.name
                    }))
                  ]}
                />
              </div>
            )}

            {/* Galenic Form */}
            {selectedSubCategory && selectedSubCategory.galenicForms.length > 0 && (
              <div>
                <label htmlFor="galenic-form-select" className="block text-sm font-normal text-gray-700 dark:text-gray-300 mb-2">
                  Forme galénique *
                </label>
                <Select
                  value={selectedGalenicForm?.id?.toString() || ''}
                  onChange={(value) => {
                    const galenicForm = selectedSubCategory.galenicForms.find(g => g.id === parseInt(value))
                    setSelectedGalenicForm(galenicForm || null)
                    setFormData(prev => ({
                      ...prev,
                      galenicFormId: galenicForm?.id
                    }))
                  }}
                  options={[
                    { value: '', label: 'Sélectionnez la forme galénique' },
                    ...selectedSubCategory.galenicForms.map(form => ({
                      value: form.id.toString(),
                      label: form.displayName || form.name
                    }))
                  ]}
                />
              </div>
            )}
          </div>
        </Card>

        {/* Manufacturing and Certifications Section */}
        <Card className="p-6">
          <h2 className="text-xl font-medium text-gray-900 dark:text-white mb-4">Fabrication et certifications</h2>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Continent */}
              <div>
                <label htmlFor="continent-select" className="block text-sm font-normal text-gray-700 dark:text-gray-300 mb-2">
                  Continent de fabrication
                </label>
                <Select
                  value={selectedContinent?.id?.toString() || ''}
                  onChange={(value) => {
                    const continent = continents.find(c => c.id === parseInt(value))
                    setSelectedContinent(continent || null)
                    setSelectedCountry(null)
                    setCountrySearchQuery('')
                    setFormData(prev => ({
                      ...prev,
                      manufacturingContinentId: continent?.id
                    }))
                  }}
                  options={[
                    { value: '', label: 'Aucun' },
                    ...continents.map(cont => ({
                      value: cont.id!.toString(),
                      label: cont.name
                    }))
                  ]}
                />
              </div>

              {/* Country Search (Helper field - not sent to backend) */}
              <div className="relative" ref={countrySearchRef}>
                <label htmlFor="country-search" className="block text-sm font-normal text-gray-700 dark:text-gray-300 mb-2">
                  Pays (aide pour sélectionner le continent)
                </label>
                <Input
                  id="country-search"
                  type="text"
                  value={countrySearchQuery}
                  onChange={(e) => {
                    setCountrySearchQuery(e.target.value)
                    setShowCountryDropdown(true)
                  }}
                  onFocus={() => setShowCountryDropdown(true)}
                  placeholder="Rechercher un pays..."
                  className="w-full"
                />
                {showCountryDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
                    {getFilteredCountries().length === 0 ? (
                      <div className="px-4 py-2 text-gray-500 dark:text-gray-400">Aucun pays trouvé</div>
                    ) : (
                      getFilteredCountries().map(country => {
                        const continent = continents.find(c => c.countries.some(co => co.id === country.id))
                        return (
                          <button
                            key={country.id}
                            type="button"
                            onClick={() => handleCountrySelect(country)}
                            className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 border-b dark:border-gray-700 last:border-b-0"
                          >
                            <div className="font-normal text-gray-900 dark:text-white">{country.name}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{continent?.name}</div>
                          </button>
                        )
                      })
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Certifications */}
            <div>
              <h3 className="text-lg font-normal text-gray-900 dark:text-white mb-3">Certifications</h3>
              <div className="flex flex-wrap gap-2">
                {certifications.map(cert => (
                  <button
                    key={cert.id}
                    type="button"
                    onClick={() => handleCertificationToggle(cert)}
                    className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                      formData.certifications.some(c => c.id === cert.id)
                      ? 'bg-sk-gold/20 dark:bg-sk-gold/10 border-sk-gold dark:border-sk-gold text-gray-900 dark:text-white'
                      : 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                      }`}
                  >
                    {cert.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Card>

        {/* Ingredients Section */}
        <Card className="p-6">
          {/* Header - Full width */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-medium text-gray-900 dark:text-white">Ingrédients</h2>
            {!isNewProduct && (
              <Button
                type="button"
                variant="primary"
                onClick={handleExtractIngredients}
                disabled={isExtracting || isNewProduct}
                className="flex items-center gap-2"
              >
                <SparklesIcon className="h-5 w-5" />
                {isExtracting
                  ? 'Extraction en cours...'
                  : isNewProduct
                    ? 'Extraction disponible après création'
                    : "Relancer l'extraction"
                }
              </Button>
            )}
          </div>

          {/* Content area with ingredients list and image */}
          <div className="flex gap-8">
            {/* Left side - Ingredients list */}
            <div className={formData.images.some(img => img.imageType === IMAGE_TYPES.BACK || img.imageType === IMAGE_TYPES.DETAILED) ? "w-1/2" : "w-full"}>
              {/* Ingredient Search */}
              <div className="relative mb-6" ref={ingredientSearchRef}>
                <label htmlFor="ingredient-search" className="block text-sm font-normal text-gray-700 dark:text-gray-300 mb-2">
                  Rechercher un ingrédient
                </label>
                <Input
                  id="ingredient-search"
                  type="text"
                  value={ingredientSearchQuery}
                  onChange={(e) => handleIngredientSearch(e.target.value)}
                  placeholder="Nom INCI ou commercial"
                  className="w-full"
                />
                {showIngredientResults && ingredientSearchResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
                    {ingredientSearchResults.map(ingredient => (
                      <button
                        key={ingredient.uuid}
                        type="button"
                        onClick={() => handleIngredientSelect(ingredient)}
                        className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 border-b dark:border-gray-700 last:border-b-0"
                      >
                        <div className="font-normal text-gray-900 dark:text-white">{ingredient.inciName}</div>
                        {ingredient.commonName && (
                          <div className="text-sm text-gray-600 dark:text-gray-400">{ingredient.commonName}</div>
                        )}
                        {ingredient.toValidate && (
                          <div className="text-xs text-red-600 dark:text-red-400">À valider</div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Selected Ingredients */}
              <div className="space-y-2">
                {formData.ingredients.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-8">Aucun ingrédient trouvé</p>
                ) : (
                  formData.ingredients.map((ingredient) => (
                    <div
                      key={ingredient.uuid}
                  className={`flex items-center justify-between p-3 border rounded-lg ${
                    ingredient.toValidate 
                        ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                    >
                      <div
                        role="button"
                        tabIndex={0}
                        className="flex-1 cursor-pointer"
                        onClick={() => {
                          if (!ingredient.toValidate) {
                            navigate(`/ingredients/${ingredient.uuid}`)
                          }
                        }}
                        onKeyDown={(e) => {
                          if ((e.key === 'Enter' || e.key === ' ') && !ingredient.toValidate) {
                            e.preventDefault()
                            navigate(`/ingredients/${ingredient.uuid}`)
                          }
                        }}
                      >
                    <div className={`font-normal ${
                      ingredient.toValidate ? 'text-red-800 dark:text-red-300' : 'text-gray-900 dark:text-white'
                          }`}>
                          {ingredient.inciName}
                        </div>
                        {ingredient.commonName && (
                      <div className={`text-sm ${
                        ingredient.toValidate ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400'
                            }`}>
                            {ingredient.commonName}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        {ingredient.toValidate && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="ml-2 text-red-600 dark:text-red-400 border-red-300 dark:border-red-700"
                            onClick={() => {
                              // Navigate to ingredient details page for validation/merge
                              navigate(`/ingredients/${ingredient.uuid}`, {
                                state: {
                                  fromProduct: true,
                                  productId: id,
                                  productName: product?.brandName || 'Produit'
                                }
                              })
                            }}
                          >
                            Valider
                          </Button>
                        )}
                        <button
                          type="button"
                          onClick={() => removeIngredient(ingredient.uuid)}
                          className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Right side - Image viewer */}
            {(formData.images.some(img => img.imageType === IMAGE_TYPES.BACK || img.imageType === IMAGE_TYPES.DETAILED)) && (
              <div className="w-1/2">
                <ImageMagnifier
                  backImage={formData.images.find(img => img.imageType === IMAGE_TYPES.BACK)}
                  detailedImage={formData.images.find(img => img.imageType === IMAGE_TYPES.DETAILED)}
                />
              </div>
            )}
          </div>
        </Card>
      </form>

      {/* Delete Confirmation Modal */}
      <ConfirmDialog
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Confirmer la suppression"
        message={`Êtes-vous sûr de vouloir supprimer le produit "${product?.brandName || ''}" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
      />

    </div>
  )
}

// Image Upload Component
interface ImageUploadSectionProps {
  type: ProductImageType
  title: string
  image?: ProductImage
  onUpload: (file: File) => void
  onRemove: () => void
  onPreview: (image: ProductImage) => void
}

const ImageUploadSection: React.FC<ImageUploadSectionProps> = ({
  title,
  image,
  onUpload,
  onRemove,
  onPreview,
}) => {
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      onUpload(file)
    }
  }

  return (
    <div className="space-y-2">
      <label className="block text-sm font-normal text-gray-700 dark:text-gray-300">{title}</label>
      {image ? (
        <div className="relative">
          <img
            src={image.image ? `data:image/jpeg;base64,${image.image}` : image.imageUrl}
            alt={title}
            className="w-full h-32 object-cover rounded-lg border dark:border-gray-600 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => onPreview(image)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onPreview(image)
              }
            }}
          />
          <button
            type="button"
            onClick={onRemove}
            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center hover:bg-red-600 shadow-lg border-2 border-white dark:border-gray-800 z-10"
          >
            <span className="text-lg font-bold leading-none">×</span>
          </button>
        </div>
      ) : (
        <label htmlFor={`file-input-${title.toLowerCase().replace(/\s+/g, '-')}`} className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700" aria-label={`Télécharger ${title}`}>
          <div className="flex flex-col items-center justify-center pt-5 pb-6">
            <svg className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
              <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"/>
            </svg>
            <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">Cliquez pour télécharger</p>
          </div>
          <input
            id={`file-input-${title.toLowerCase().replace(/\s+/g, '-')}`}
            type="file"
            className="hidden"
            accept="image/*"
            onChange={handleFileChange}
          />
        </label>
      )}
    </div>
  )
}